import Autocomplete from "@/atoms/autocomplete";
import BackButton from "@/atoms/backButton";
import {
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@/common/styles/style";
import { styles } from "./style";
import { useNavigate } from "react-router-dom";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Routes } from "@/router/routes";
import { TenantsType, GroupsType } from "@/common/type/type";
import ErrorMessage from "@/atoms/errorMessage";

export default function MachineRegister() {
  const navigate = useNavigate();
  const [selectedTenant, setSelectedTenant] = useState<string>("");
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [machineNumber, setMachineNumber] = useState<string>("");
  const [tenantOptions, setTenantOptions] = useState<string[]>([]);
  const [groupOptions, setGroupOptions] = useState<string[]>([]);
  const [dataResult, setDataResult] = useState<TenantsType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedTenantData, setSelectedTenantData] =
    useState<TenantsType | null>(null);

  useEffect(() => {
    const checkServerConnection = async () => {
      try {
        const response = await fetch("/api/machine/edit/", {
          method: "GET",
          credentials: "include",
        });

        const data = await response.json();
        if (response.ok) {
          console.log(data.message);
          setDataResult(data.result.tenants);
          setTenantOptions(
            data.result.tenants.map((tenant: TenantsType) => tenant.name)
          );
        } else if (response.status === 404) {
          alert(data.message);
          navigate(Routes.ERROR);
        } else if (response.status === 401 || response.status === 403) {
          alert(data.message);
          navigate(Routes.LOGIN);
        } else {
          setError(data.message);
        }
      } catch (error) {
        console.error("サーバ接続エラー:", error);
      }
    };

    checkServerConnection();
  }, []);

  useEffect(() => {
    if (selectedTenant) {
      const tenantData = dataResult.find(
        (tenant: TenantsType) => tenant.name === selectedTenant
      );
      if (tenantData) {
        setSelectedTenantData(tenantData);
        const groups = tenantData.groups.map((group: GroupsType) => group.name);
        setGroupOptions(groups);
      } else {
        setGroupOptions([]);
      }
    }
  }, [selectedTenant]);

  const handleDelete = (chip: string, type: "tenant" | "group") => {
    if (type === "tenant") {
      setSelectedTenant("");
    } else {
      setSelectedGroups((prev) => prev.filter((item) => item !== chip));
    }
  };

  const handleClose = () => {
    setError("機械情報の登録をキャンセルしました。");
    navigate(Routes.MACHINE_LIST);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!machineNumber || !selectedTenant) {
      setError("すべての項目を入力してください。");
      return;
    }

    const tenant = dataResult.find((tenant) => tenant.name === selectedTenant);
    if (!tenant) {
      setError("選択されたテナントが存在しません。");
      return;
    }
    const requestBody = {
      machineNumber: machineNumber,
      tenantId: tenant.id,
      tenantName: selectedTenant,
      groupIds:
        (selectedTenantData as TenantsType).groups
          .filter((group) => selectedGroups.includes(group.name))
          .map((group) => group.id).length > 0
          ? (selectedTenantData as TenantsType).groups
              .filter((group) => selectedGroups.includes(group.name))
              .map((group) => group.id)
          : null,
    };

    console.log("requestBody", requestBody);
    try {
      const response = await fetch("/api/machine", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (response.status === 201) {
        console.log(data.message);
        navigate(Routes.MACHINE_LIST);
        setMachineNumber("");
        setSelectedTenant("");
        setSelectedGroups([]);
      } else {
        alert(data.message)
      }
    } catch (error) {
      console.error("API呼び出しエラー:", error);
      alert("機械登録中にエラーが発生しました。");
    }
  };

  const renderEditableField = (
    title: string,
    options: string[],
    selected: string | string[],
    setSelected: React.Dispatch<React.SetStateAction<string | string[]>>
  ) => (
    <Stack direction="row" spacing={5}>
      <Typography variant="h5" sx={styles.listTitle}>
        {title}
      </Typography>
      <Stack sx={styles.autocompleteArea}>
        <Autocomplete
          multiple={title === "所属グループ" ? true : undefined}
          options={options}
          getOptionLabel={(option) =>
            Array.isArray(option) ? option[0] : option
          }
          fullWidth
          value={Array.isArray(selected) ? selected : [selected]}
          onChange={(_, newValue: unknown) => {
            if (title === "所属テナント") {
              setSelected(newValue as string);
            } else {
              setSelected(newValue as string[]);
            }
          }}
          label={`Select ${title}`}
          placeholder={`Search ${title}`}
          renderTags={() => null}
          size="small"
        />

        <Box sx={styles.chipsbox}>
          {selected &&
            (Array.isArray(selected) ? selected : [selected]).map((chip) => (
              <Chip
                key={chip}
                label={chip}
                onDelete={() =>
                  handleDelete(
                    chip,
                    title === "所属テナント" ? "tenant" : "group"
                  )
                }
                sx={styles.chips}
                size="small"
              />
            ))}
        </Box>
      </Stack>
    </Stack>
  );

  return (
    <Box sx={styles.container}>
      <ErrorMessage error={error} />
      <Stack direction="row" spacing={3} sx={styles.titleArea}>
        <BackButton />
        <Typography variant="h3" sx={styles.title}>
          新しい機械登録
        </Typography>
      </Stack>
      <form onSubmit={handleSubmit}>
        <Stack spacing={10} sx={styles.form}>
          <Stack direction="row" spacing={5}>
            <Typography variant="h5" sx={styles.listTitle}>
              機器番号
            </Typography>
            <TextField
              fullWidth
              value={machineNumber}
              onChange={(e) => setMachineNumber(e.target.value)}
              variant="outlined"
              size="small"
            />
          </Stack>
          {renderEditableField(
            "所属テナント",
            tenantOptions,
            selectedTenant,
            setSelectedTenant as Dispatch<SetStateAction<string | string[]>>
          )}
          {renderEditableField(
            "所属グループ",
            groupOptions,
            selectedGroups,
            setSelectedGroups as Dispatch<SetStateAction<string | string[]>>
          )}
        </Stack>
        <Stack direction="row" sx={styles.buttonArea}>
          <Button variant="outlined" onClick={handleClose}>
            キャンセル
          </Button>
          <Button variant="contained" type="submit">
            保存
          </Button>
        </Stack>
      </form>
    </Box>
  );
}
